import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  LayoutDashboard, 
  DollarSign,
  Zap,
  CheckCircle2,
  XCircle,
  TrendingUp,
  ArrowLeft,
  RefreshCw,
  Activity,
  BarChart3,
  Clock,
  FileText,
  Lock,
  ShieldCheck,
  UserX,
  MessageSquare,
  PieChart,
  Search,
  FolderSearch,
  Download,
  ExternalLink,
  File,
  User,
  Building2,
  Loader2
} from "lucide-react";
import Logo from "../components/Logo";
import AccountMonitoringPage from "./AccountMonitoringPage";

export default function AdminDashboardPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Admin access is now verified via user.is_admin flag
  const isAdmin = user?.is_admin === true;
  
  // PIN-based session authentication (resets on page refresh)
  const [pinEntered, setPinEntered] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [verifyingPin, setVerifyingPin] = useState(false);
  
  // Dashboard state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [usageLogs, setUsageLogs] = useState([]);
  const [filter, setFilter] = useState("all"); // all, success, failed
  const [pagination, setPagination] = useState({ limit: 50, skip: 0 });
  const [activeTab, setActiveTab] = useState("usage"); // usage, cancellations, monitoring, filerecovery, reconciliation
  
  // Cancellations state
  const [cancellationsSummary, setCancellationsSummary] = useState(null);
  const [cancellations, setCancellations] = useState([]);
  const [cancellationsLoading, setCancellationsLoading] = useState(false);
  
  // Reconciliation state
  const [reconciliationStats, setReconciliationStats] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  
  // Account monitoring badge count
  const [highRiskCount, setHighRiskCount] = useState(0);

  // Redirect non-admin users
  useEffect(() => {
    if (user && !isAdmin) {
      toast.error("Admin access required");
      navigate("/");
    }
  }, [user, isAdmin, navigate]);

  // Verify PIN
  const verifyPin = async () => {
    if (!pinInput.trim()) {
      setPinError("Please enter the admin PIN");
      return;
    }
    
    setVerifyingPin(true);
    setPinError("");
    
    try {
      await axios.post(`${API}/admin/verify-pin`, {
        pin: pinInput
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPinEntered(true);
      setPinInput("");
      toast.success("Admin access granted");
    } catch (error) {
      setPinError(error.response?.data?.detail || "Invalid PIN");
      setPinInput("");
    } finally {
      setVerifyingPin(false);
    }
  };

  // Fetch data
  const fetchData = async (showRefreshToast = false) => {
    if (!isAdmin || !pinEntered) return;
    
    try {
      if (showRefreshToast) setRefreshing(true);
      
      // Fetch summary
      const summaryRes = await axios.get(`${API}/admin/llm-usage/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(summaryRes.data);
      
      // Fetch logs with filter
      let logsUrl = `${API}/admin/llm-usage?limit=${pagination.limit}&skip=${pagination.skip}`;
      if (filter === "success") logsUrl += "&success_only=true";
      if (filter === "failed") logsUrl += "&success_only=false";
      
      const logsRes = await axios.get(logsUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageLogs(logsRes.data.logs);
      
      if (showRefreshToast) toast.success("Data refreshed");
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch cancellations data
  const fetchCancellationsData = async () => {
    try {
      setCancellationsLoading(true);
      
      const [summaryRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/cancellations/summary`),
        axios.get(`${API}/admin/cancellations?limit=50`)
      ]);
      
      setCancellationsSummary(summaryRes.data);
      setCancellations(listRes.data.cancellations);
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      toast.error("Failed to load cancellation data");
    } finally {
      setCancellationsLoading(false);
    }
  };

  // Fetch high risk count for badge
  const fetchHighRiskCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/high-risk-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHighRiskCount(response.data.high_risk || 0);
    } catch (error) {
      console.error("Error fetching high risk count:", error);
    }
  };

  // Only fetch data when admin is verified AND PIN is entered
  useEffect(() => {
    if (isAdmin && pinEntered) {
      fetchData();
      fetchHighRiskCount();
    }
  }, [filter, isAdmin, pinEntered]);

  // Fetch cancellations when tab changes
  useEffect(() => {
    if (isAdmin && pinEntered && activeTab === "cancellations" && !cancellationsSummary) {
      fetchCancellationsData();
    }
  }, [activeTab, isAdmin, pinEntered]);

  // Fetch reconciliation stats when tab changes
  useEffect(() => {
    if (isAdmin && pinEntered && activeTab === "reconciliation" && !reconciliationStats) {
      fetchReconciliationStats();
    }
  }, [activeTab, isAdmin, pinEntered]);

  // Fetch reconciliation dashboard stats
  const fetchReconciliationStats = async () => {
    setReconciliationLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reconciliation-dashboard?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReconciliationStats(response.data);
    } catch (error) {
      console.error("Error fetching reconciliation stats:", error);
      toast.error("Failed to fetch reconciliation stats");
    } finally {
      setReconciliationLoading(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Format cost
  const formatCost = (cost) => {
    if (cost < 0.01) {
      return `$${cost.toFixed(4)}`;
    }
    return `$${cost.toFixed(2)}`;
  };

  // Access Denied Screen for non-admins
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <Card className="w-full max-w-md mx-4 bg-white border-[#e5e5e0] shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-[#1a4d2e]/10 rounded-full flex items-center justify-center">
              <Lock className="h-8 w-8 text-[#1a4d2e]" />
            </div>
            <CardTitle className="text-2xl text-[#1a4d2e]">Authentication Required</CardTitle>
            <CardDescription>Please log in to access the admin dashboard</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => navigate("/login")}
              className="bg-[#1a4d2e] hover:bg-[#2d6a4f]"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <Card className="w-full max-w-md mx-4 bg-white border-[#e5e5e0] shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <UserX className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-600">Access Denied</CardTitle>
            <CardDescription>You do not have admin privileges</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-500">
              Logged in as: <span className="font-medium">{user.email}</span>
            </p>
            <div className="flex gap-3 justify-center">
              <Button 
                variant="outline"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
              <Button 
                variant="outline"
                onClick={logout}
                className="text-red-600 hover:text-red-700"
              >
                Log Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // PIN Entry Screen - required on every page load
  if (!pinEntered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <Card className="w-full max-w-md mx-4 bg-white border-[#e5e5e0] shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-[#1a4d2e]/10 rounded-full flex items-center justify-center">
              <Lock className="h-8 w-8 text-[#1a4d2e]" />
            </div>
            <CardTitle className="text-2xl text-[#1a4d2e]">Admin PIN Required</CardTitle>
            <CardDescription>Enter your admin PIN to continue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-pin">Admin PIN</Label>
              <Input
                id="admin-pin"
                type="password"
                placeholder="Enter PIN"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && verifyPin()}
                className={pinError ? "border-red-500" : ""}
                data-testid="admin-pin-input"
              />
              {pinError && (
                <p className="text-sm text-red-600">{pinError}</p>
              )}
            </div>
            <Button 
              onClick={verifyPin}
              disabled={verifyingPin}
              className="w-full bg-[#1a4d2e] hover:bg-[#2d6a4f]"
              data-testid="admin-pin-submit"
            >
              {verifyingPin ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              Verify PIN
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Session expires on page refresh
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a4d2e]"></div>
          <p className="text-[#1a4d2e] font-medium">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      {/* Header */}
      <header className="bg-white border-b border-[#e5e5e0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2 text-[#666] hover:text-[#1a4d2e] transition-colors">
                <ArrowLeft className="h-5 w-5" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </Link>
              <div className="h-6 w-px bg-[#e5e5e0]"></div>
              <div className="flex items-center gap-2">
                <Logo className="h-8 w-8" />
                <span className="font-semibold text-[#1a4d2e]">Admin</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title & Tabs */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#1a4d2e]">Admin Dashboard</h1>
          <p className="text-[#666] mt-1">Monitor AI costs, usage, and business health</p>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-6 border-b border-[#e5e5e0]">
            <button
              onClick={() => setActiveTab("usage")}
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === "usage"
                  ? "text-[#1a4d2e] border-b-2 border-[#1a4d2e]"
                  : "text-[#666] hover:text-[#1a4d2e]"
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                LLM Usage
              </div>
            </button>
            <button
              onClick={() => setActiveTab("cancellations")}
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === "cancellations"
                  ? "text-[#1a4d2e] border-b-2 border-[#1a4d2e]"
                  : "text-[#666] hover:text-[#1a4d2e]"
              }`}
            >
              <div className="flex items-center gap-2">
                <UserX className="h-4 w-4" />
                Cancellations
              </div>
            </button>
            <button
              onClick={() => setActiveTab("monitoring")}
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === "monitoring"
                  ? "text-[#1a4d2e] border-b-2 border-[#1a4d2e]"
                  : "text-[#666] hover:text-[#1a4d2e]"
              }`}
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Account Monitoring
                {highRiskCount > 0 && (
                  <Badge className="bg-red-500 text-white text-xs ml-1">{highRiskCount}</Badge>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab("filerecovery")}
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === "filerecovery"
                  ? "text-[#1a4d2e] border-b-2 border-[#1a4d2e]"
                  : "text-[#666] hover:text-[#1a4d2e]"
              }`}
            >
              <div className="flex items-center gap-2">
                <FolderSearch className="h-4 w-4" />
                File Recovery
              </div>
            </button>
            <button
              onClick={() => setActiveTab("reconciliation")}
              className={`pb-3 px-1 font-medium transition-colors ${
                activeTab === "reconciliation"
                  ? "text-[#1a4d2e] border-b-2 border-[#1a4d2e]"
                  : "text-[#666] hover:text-[#1a4d2e]"
              }`}
            >
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Reconciliation
              </div>
            </button>
          </div>
        </div>

        {/* LLM Usage Tab */}
        {activeTab === "usage" && (
          <>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Spent */}
          <Card className="bg-white border-[#e5e5e0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-[#1a4d2e]" />
                Total Spent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#1a4d2e]">
                {formatCost(summary?.overall?.total_cost_estimate_usd || 0)}
              </div>
              <p className="text-xs text-[#888] mt-1">Estimated cost (GPT-4o-mini)</p>
            </CardContent>
          </Card>

          {/* Total Calls */}
          <Card className="bg-white border-[#e5e5e0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#f59e0b]" />
                Total AI Calls
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#1a1a1a]">
                {summary?.overall?.total_calls || 0}
              </div>
              <p className="text-xs text-[#888] mt-1">Lead scoring requests</p>
            </CardContent>
          </Card>

          {/* Success Rate */}
          <Card className="bg-white border-[#e5e5e0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#22c55e]" />
                Success Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#22c55e]">
                {summary?.overall?.success_rate || 0}%
              </div>
              <p className="text-xs text-[#888] mt-1">
                {summary?.overall?.successful_calls || 0} successful / {summary?.overall?.failed_calls || 0} failed
              </p>
            </CardContent>
          </Card>

          {/* Total Tokens */}
          <Card className="bg-white border-[#e5e5e0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                <Activity className="h-4 w-4 text-[#8b5cf6]" />
                Tokens Used
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#1a1a1a]">
                {(summary?.overall?.total_tokens_estimate || 0).toLocaleString()}
              </div>
              <p className="text-xs text-[#888] mt-1">Estimated token usage</p>
            </CardContent>
          </Card>
        </div>

        {/* Daily Breakdown Chart */}
        <Card className="bg-white border-[#e5e5e0] shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#1a4d2e]" />
              Last 7 Days Usage
            </CardTitle>
            <CardDescription>Daily AI calls and costs</CardDescription>
          </CardHeader>
          <CardContent>
            {summary?.daily_breakdown?.length > 0 ? (
              <div className="space-y-4">
                {/* Simple bar visualization */}
                <div className="grid grid-cols-7 gap-2">
                  {summary.daily_breakdown.slice().reverse().map((day, index) => {
                    const maxCalls = Math.max(...summary.daily_breakdown.map(d => d.calls));
                    const heightPercent = maxCalls > 0 ? (day.calls / maxCalls) * 100 : 0;
                    return (
                      <div key={day.date} className="flex flex-col items-center">
                        <div className="h-32 w-full flex items-end justify-center">
                          <div 
                            className="w-full max-w-[40px] bg-[#1a4d2e] rounded-t-md transition-all duration-300 hover:bg-[#2d6a4f]"
                            style={{ height: `${Math.max(heightPercent, 5)}%` }}
                            title={`${day.calls} calls - ${formatCost(day.cost_usd)}`}
                          ></div>
                        </div>
                        <div className="mt-2 text-center">
                          <p className="text-xs font-medium text-[#1a1a1a]">{day.calls}</p>
                          <p className="text-xs text-[#888]">{new Date(day.date).toLocaleDateString("en-US", { weekday: "short" })}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex items-center justify-center gap-6 pt-4 border-t border-[#e5e5e0]">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#1a4d2e] rounded"></div>
                    <span className="text-sm text-[#666]">AI Calls</span>
                  </div>
                  <div className="text-sm text-[#888]">
                    7-day total: {formatCost(summary.daily_breakdown.reduce((sum, d) => sum + d.cost_usd, 0))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-[#888]">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No usage data in the last 7 days</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rolling Averages & Top Consumers Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Rolling Averages */}
          <Card className="bg-white border-[#e5e5e0] shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#1a4d2e]" />
                7-Day Averages
              </CardTitle>
              <CardDescription>Performance metrics per lead</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-[#f8f8f5] rounded-lg">
                  <p className="text-3xl font-bold text-[#1a4d2e]">
                    {summary?.rolling_averages?.avg_tokens_per_lead?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-[#666] mt-1">Avg Tokens/Lead</p>
                </div>
                <div className="text-center p-4 bg-[#f8f8f5] rounded-lg">
                  <p className="text-3xl font-bold text-[#1a4d2e]">
                    {formatCost(summary?.rolling_averages?.avg_cost_per_lead || 0)}
                  </p>
                  <p className="text-sm text-[#666] mt-1">Avg Cost/Lead</p>
                </div>
              </div>
              
              {/* Safety Events */}
              {summary?.safety_events && Object.keys(summary.safety_events).length > 0 && (
                <div className="mt-6 pt-4 border-t border-[#e5e5e0]">
                  <p className="text-sm font-medium text-[#666] mb-3">Safety Events</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(summary.safety_events).map(([event, count]) => (
                      <Badge 
                        key={event} 
                        variant="outline" 
                        className={`${event.includes('exceeded') || event.includes('disabled') ? 'border-red-300 text-red-700' : 'border-yellow-300 text-yellow-700'}`}
                      >
                        {event}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Consumers */}
          <Card className="bg-white border-[#e5e5e0] shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#8b5cf6]" />
                Top AI Consumers (7 days)
              </CardTitle>
              <CardDescription>Businesses with highest token usage</CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.top_consumers?.length > 0 ? (
                <div className="space-y-3">
                  {summary.top_consumers.map((consumer, index) => (
                    <div key={consumer.business_id} className="flex items-center justify-between p-3 bg-[#f8f8f5] rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 flex items-center justify-center bg-[#1a4d2e] text-white text-xs font-bold rounded-full">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-[#1a1a1a]">{consumer.business_name}</p>
                          <p className="text-xs text-[#888]">{consumer.calls} calls</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-medium text-[#1a4d2e]">{formatCost(consumer.cost_usd)}</p>
                        <p className="text-xs text-[#888]">{consumer.tokens?.toLocaleString()} tokens</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#888]">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No usage data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* System Config Banner */}
        {summary?.config && (
          <Card className="bg-[#f0fdf4] border-[#86efac] shadow-sm mb-8">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${summary.config.ai_scoring_enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm font-medium">
                    AI Scoring: {summary.config.ai_scoring_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-[#666]">
                  <span>Max Tokens: {summary.config.max_tokens_per_request?.toLocaleString()}/request</span>
                  <span>Daily Limit: {summary.config.daily_limit_per_business}/business</span>
                  <span>Cost: ${summary.config.cost_per_1k_tokens}/1K tokens</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Usage Logs Table */}
        <Card className="bg-white border-[#e5e5e0] shadow-sm">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#1a4d2e]" />
                  Usage per Form Submission
                </CardTitle>
                <CardDescription>Detailed log of each AI scoring call</CardDescription>
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calls</SelectItem>
                  <SelectItem value="success">Successful</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {usageLogs.length > 0 ? (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#f8f8f5]">
                      <TableHead className="font-semibold text-[#1a4d2e]">Timestamp</TableHead>
                      <TableHead className="font-semibold text-[#1a4d2e]">Lead ID</TableHead>
                      <TableHead className="font-semibold text-[#1a4d2e]">Model</TableHead>
                      <TableHead className="font-semibold text-[#1a4d2e] text-right">Tokens</TableHead>
                      <TableHead className="font-semibold text-[#1a4d2e] text-right">Cost</TableHead>
                      <TableHead className="font-semibold text-[#1a4d2e] text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usageLogs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-[#f8f8f5]">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-[#888]" />
                            {formatTimestamp(log.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-[#f0f0eb] px-2 py-1 rounded">
                            {log.lead_id?.slice(0, 8) || "N/A"}...
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {log.model}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {log.total_tokens_estimate?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right font-mono text-[#1a4d2e] font-medium">
                          {formatCost(log.cost_estimate_usd || 0)}
                        </TableCell>
                        <TableCell className="text-center">
                          {log.success ? (
                            <Badge className="bg-[#dcfce7] text-[#166534] hover:bg-[#dcfce7]">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Success
                            </Badge>
                          ) : (
                            <Badge className="bg-[#fee2e2] text-[#991b1b] hover:bg-[#fee2e2]">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : (
              <div className="text-center py-12 text-[#888]">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No usage logs found</p>
                <p className="text-sm mt-1">Submit a lead to start tracking AI usage</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Footer */}
        <div className="mt-8 p-4 bg-white rounded-lg border border-[#e5e5e0] text-center">
          <p className="text-sm text-[#888]">
            Cost estimates are based on GPT-4o-mini pricing (~${summary?.cost_per_1k_tokens || 0.0003}/1K tokens). 
            Actual costs may vary slightly.
          </p>
        </div>
          </>
        )}

        {/* Cancellations Tab */}
        {activeTab === "cancellations" && (
          <>
            {cancellationsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a4d2e]"></div>
              </div>
            ) : (
              <>
                {/* Cancellations Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <UserX className="h-4 w-4 text-[#ef4444]" />
                        Total Cancellations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#1a1a1a]">
                        {cancellationsSummary?.overview?.total_cancellations || 0}
                      </div>
                      <p className="text-xs text-[#888] mt-1">All time</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-[#f59e0b]" />
                        Last 30 Days
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#1a1a1a]">
                        {cancellationsSummary?.overview?.last_30_days || 0}
                      </div>
                      <p className="text-xs text-[#888] mt-1">
                        {cancellationsSummary?.overview?.last_7_days || 0} in last 7 days
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#8b5cf6]" />
                        Trial Cancellations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#1a1a1a]">
                        {cancellationsSummary?.overview?.trial_cancellations || 0}
                      </div>
                      <p className="text-xs text-[#888] mt-1">
                        {cancellationsSummary?.overview?.trial_percentage || 0}% of total
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <Activity className="h-4 w-4 text-[#22c55e]" />
                        Active Cancellations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#1a1a1a]">
                        {cancellationsSummary?.overview?.active_cancellations || 0}
                      </div>
                      <p className="text-xs text-[#888] mt-1">Paying customers who left</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Reason Breakdown */}
                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-[#1a4d2e]" />
                        Cancellation Reasons
                      </CardTitle>
                      <CardDescription>Why customers are leaving</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {cancellationsSummary?.reason_breakdown?.length > 0 ? (
                        <div className="space-y-3">
                          {cancellationsSummary.reason_breakdown.map((item, index) => (
                            <div key={item.reason} className="flex items-center gap-3">
                              <div className="w-full">
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="font-medium">{item.reason_label}</span>
                                  <span className="text-[#666]">{item.count} ({item.percentage}%)</span>
                                </div>
                                <div className="h-2 bg-[#f0f0f0] rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-[#1a4d2e] rounded-full transition-all"
                                    style={{ width: `${item.percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-[#888]">
                          <PieChart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No feedback data yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Daily Trend */}
                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-[#8b5cf6]" />
                        Cancellation Trend (30 days)
                      </CardTitle>
                      <CardDescription>Daily cancellations over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {cancellationsSummary?.daily_trend?.length > 0 ? (
                        <div className="h-48 flex items-end gap-1">
                          {cancellationsSummary.daily_trend.map((day) => {
                            const maxValue = Math.max(...cancellationsSummary.daily_trend.map(d => d.total), 1);
                            const height = (day.total / maxValue) * 100;
                            return (
                              <div key={day.date} className="flex-1 group relative">
                                <div 
                                  className="bg-[#1a4d2e] rounded-t transition-all hover:bg-[#2d6a4f]"
                                  style={{ height: `${Math.max(height, 4)}%` }}
                                ></div>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                  <div className="bg-[#1a1a1a] text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                    {day.date}: {day.total} cancellation{day.total !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-[#888]">
                          <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No trend data available</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Comments */}
                {cancellationsSummary?.recent_with_comments?.length > 0 && (
                  <Card className="bg-white border-[#e5e5e0] shadow-sm mb-8">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-[#f59e0b]" />
                        Recent Feedback
                      </CardTitle>
                      <CardDescription>Customer comments from cancellations</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {cancellationsSummary.recent_with_comments.map((item) => (
                          <div key={item.id} className="p-4 bg-[#f8f8f5] rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium text-[#1a1a1a]">{item.business_name}</p>
                                <p className="text-xs text-[#888]">{item.email}</p>
                              </div>
                              <div className="text-right">
                                <Badge variant="outline" className="text-xs">
                                  {item.reason_label}
                                </Badge>
                                <p className="text-xs text-[#888] mt-1">
                                  {new Date(item.canceled_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm text-[#666] italic">"{item.comment}"</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Cancellations List */}
                <Card className="bg-white border-[#e5e5e0] shadow-sm">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>All Cancellations</CardTitle>
                        <CardDescription>Complete history of subscription cancellations</CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchCancellationsData}
                        className="border-[#1a4d2e] text-[#1a4d2e]"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {cancellations.length > 0 ? (
                      <ScrollArea className="h-[400px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Business</TableHead>
                              <TableHead>Plan</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Reason</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {cancellations.map((c) => (
                              <TableRow key={c.id}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium">{c.business_name}</p>
                                    <p className="text-xs text-[#888]">{c.email}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="capitalize">
                                    {c.plan || "Unknown"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {c.type === "account_deletion" ? (
                                    <Badge className="bg-[#fee2e2] text-[#991b1b]">Deleted</Badge>
                                  ) : c.is_trialing ? (
                                    <Badge className="bg-[#fef3c7] text-[#92400e]">Trial</Badge>
                                  ) : (
                                    <Badge className="bg-[#dbeafe] text-[#1e40af]">Active</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm">{c.reason_label}</span>
                                  {c.comment && (
                                    <p className="text-xs text-[#888] mt-1 max-w-xs truncate" title={c.comment}>
                                      "{c.comment}"
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm text-[#666]">
                                  {new Date(c.canceled_at).toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    ) : (
                      <div className="text-center py-12 text-[#888]">
                        <UserX className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No cancellations recorded</p>
                        <p className="text-sm mt-1">Great news - no one has canceled yet!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {/* Account Monitoring Tab */}
        {activeTab === "monitoring" && (
          <AccountMonitoringPage />
        )}

        {/* File Recovery Tab */}
        {activeTab === "filerecovery" && (
          <FileRecoveryTab />
        )}

        {/* Reconciliation Tab */}
        {activeTab === "reconciliation" && (
          <div className="space-y-6">
            {reconciliationLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#1a4d2e]" />
                <span className="ml-2 text-[#666]">Loading reconciliation stats...</span>
              </div>
            ) : reconciliationStats ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Total Runs (30d)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#1a4d2e]">
                        {reconciliationStats.total_runs}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#e5e5e0] shadow-sm border-l-4 border-l-red-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        Critical Issues
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-600">
                        {reconciliationStats.total_critical_issues}
                      </div>
                      <p className="text-xs text-red-600 mt-1">Requires attention (status/plan mismatch)</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#e5e5e0] shadow-sm border-l-4 border-l-green-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Silent Fixes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-600">
                        {reconciliationStats.total_silent_fixes}
                      </div>
                      <p className="text-xs text-green-600 mt-1">Auto-corrected (timestamps, flags)</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-[#666] flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Accounts Checked
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-[#1a4d2e]">
                        {reconciliationStats.total_accounts_checked}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Field Classification */}
                <Card className="bg-white border-[#e5e5e0] shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-[#1a4d2e]">Field Classification</CardTitle>
                    <CardDescription>How fields are classified for alerting vs silent sync</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                          <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                          CRITICAL Fields (Trigger Alerts)
                        </h4>
                        <ul className="text-sm text-red-700 space-y-1">
                          {reconciliationStats.field_classification?.critical_fields?.map(field => (
                            <li key={field} className="font-mono bg-red-100 px-2 py-1 rounded inline-block mr-2 mb-1">{field}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                          <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                          NON-CRITICAL Fields (Silent Sync)
                        </h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          {reconciliationStats.field_classification?.non_critical_fields?.map(field => (
                            <li key={field} className="font-mono bg-green-100 px-2 py-1 rounded inline-block mr-2 mb-1">{field}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Field Breakdown */}
                {Object.keys(reconciliationStats.field_breakdown || {}).length > 0 && (
                  <Card className="bg-white border-[#e5e5e0] shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-[#1a4d2e]">Issue Breakdown by Field</CardTitle>
                      <CardDescription>Which fields have been corrected in the last 30 days</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Field</TableHead>
                            <TableHead className="text-center">Critical Issues</TableHead>
                            <TableHead className="text-center">Silent Fixes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(reconciliationStats.field_breakdown || {}).map(([field, counts]) => (
                            <TableRow key={field}>
                              <TableCell className="font-mono text-sm">{field}</TableCell>
                              <TableCell className="text-center">
                                {counts.critical > 0 ? (
                                  <Badge className="bg-red-100 text-red-800">{counts.critical}</Badge>
                                ) : (
                                  <span className="text-gray-400">0</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {counts.non_critical > 0 ? (
                                  <Badge className="bg-green-100 text-green-800">{counts.non_critical}</Badge>
                                ) : (
                                  <span className="text-gray-400">0</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Runs */}
                <Card className="bg-white border-[#e5e5e0] shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-[#1a4d2e]">Recent Runs</CardTitle>
                    <CardDescription>Last 10 reconciliation job executions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Run Time</TableHead>
                          <TableHead className="text-center">Accounts</TableHead>
                          <TableHead className="text-center">Critical</TableHead>
                          <TableHead className="text-center">Silent Fixes</TableHead>
                          <TableHead className="text-center">Errors</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reconciliationStats.recent_runs?.map((run, idx) => (
                          <TableRow key={run.run_id || idx}>
                            <TableCell className="text-sm">
                              {new Date(run.started_at).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </TableCell>
                            <TableCell className="text-center">{run.total_checked}</TableCell>
                            <TableCell className="text-center">
                              {run.critical > 0 ? (
                                <Badge className="bg-red-100 text-red-800">{run.critical}</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">0</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {run.silent_fixes > 0 ? (
                                <Badge className="bg-green-100 text-green-800">{run.silent_fixes}</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">0</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {run.errors > 0 ? (
                                <Badge className="bg-yellow-100 text-yellow-800">{run.errors}</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">0</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Refresh Button */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReconciliationStats(null);
                      fetchReconciliationStats();
                    }}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh Data
                  </Button>
                </div>
              </>
            ) : (
              <Card className="bg-white border-[#e5e5e0] shadow-sm">
                <CardContent className="py-16 text-center">
                  <p className="text-[#666]">No reconciliation data available.</p>
                  <Button
                    variant="outline"
                    onClick={fetchReconciliationStats}
                    className="mt-4"
                  >
                    Load Data
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ===================== FILE RECOVERY TAB COMPONENT =====================

function FileRecoveryTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API}/admin/documents/search?query=${encodeURIComponent(searchQuery.trim())}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setSearchResults(response.data);
      if (response.data.results?.length === 0) {
        toast.info("No documents found for this search");
      } else {
        toast.success(`Found ${response.data.total_documents || 0} documents`);
      }
    } catch (err) {
      console.error("File recovery search error:", err);
      setError(err.response?.data?.detail || "Search failed");
      toast.error("Search failed");
    } finally {
      setIsSearching(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    const colors = {
      uploaded: "bg-green-100 text-green-800",
      reviewed: "bg-blue-100 text-blue-800",
      pending: "bg-yellow-100 text-yellow-800",
      missing: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <Card className="bg-white border-[#e5e5e0]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1a4d2e] flex items-center gap-2">
            <FolderSearch className="h-5 w-5" />
            File Recovery Tool
          </CardTitle>
          <CardDescription>
            Search by email or contact name to find all associated documents across the system.
            This is a read-only debugging tool.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="file-recovery-search-input"
              />
            </div>
            <Button
              type="submit"
              disabled={isSearching || searchQuery.trim().length < 2}
              className="bg-[#1a4d2e] hover:bg-[#2d5a3d]"
              data-testid="file-recovery-search-button"
            >
              {isSearching ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {searchResults && (
        <div className="space-y-4">
          {/* Summary */}
          {searchResults.results?.length > 0 && (
            <div className="text-sm text-gray-500">
              Found {searchResults.total_contacts || searchResults.results.length} contact(s) with {searchResults.total_documents || 0} document(s)
            </div>
          )}

          {/* Empty State */}
          {searchResults.results?.length === 0 && (
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-8 text-center">
                <FolderSearch className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No documents found for "{searchResults.query}"</p>
                <p className="text-sm text-gray-400 mt-2">Try searching with a different email or name</p>
              </CardContent>
            </Card>
          )}

          {/* Results List */}
          {searchResults.results?.map((result, resultIdx) => (
            <Card key={resultIdx} className="bg-white border-[#e5e5e0]">
              <CardHeader className="pb-3">
                {/* Business & Contact Info */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-[#1a4d2e]" />
                    <span className="font-medium">{result.business?.name || "Unknown Business"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">{result.contact?.name || "Unknown"}</span>
                    <span className="text-gray-500">({result.contact?.email || "No email"})</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Submissions */}
                {result.submissions?.map((submission, subIdx) => (
                  <div key={subIdx} className="border-t border-gray-100 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
                    {/* Submission Header */}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                      <FileText className="h-3 w-3" />
                      <span>Submission: {submission.submission_id?.slice(0, 8)}...</span>
                      <span>•</span>
                      <span>{formatDate(submission.created_at)}</span>
                      {submission.lead_status && (
                        <>
                          <span>•</span>
                          <Badge variant="outline" className="text-xs">{submission.lead_status}</Badge>
                        </>
                      )}
                      {submission.intake_stage && (
                        <>
                          <span>•</span>
                          <Badge variant="outline" className="text-xs">{submission.intake_stage}</Badge>
                        </>
                      )}
                    </div>

                    {/* Documents Table */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-xs">Document</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs">Uploaded</TableHead>
                            <TableHead className="text-xs">Size</TableHead>
                            <TableHead className="text-xs text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {submission.documents?.map((doc, docIdx) => (
                            <TableRow key={docIdx}>
                              <TableCell className="py-2">
                                <div className="flex items-center gap-2">
                                  <File className="h-4 w-4 text-gray-400" />
                                  <div>
                                    <div className="font-medium text-sm">{doc.label || doc.document_type}</div>
                                    {doc.file_name && (
                                      <div className="text-xs text-gray-500">{doc.file_name}</div>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-2">
                                <Badge className={`text-xs ${getStatusBadge(doc.status)}`}>
                                  {doc.status || "unknown"}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2 text-xs text-gray-500">
                                {formatDate(doc.uploaded_at)}
                              </TableCell>
                              <TableCell className="py-2 text-xs text-gray-500">
                                {formatFileSize(doc.file_size)}
                              </TableCell>
                              <TableCell className="py-2 text-right">
                                {doc.signed_url ? (
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(doc.signed_url, "_blank")}
                                      className="h-7 px-2 text-xs"
                                      data-testid={`view-doc-${docIdx}`}
                                    >
                                      <ExternalLink className="h-3 w-3 mr-1" />
                                      View
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const link = document.createElement("a");
                                        link.href = doc.signed_url;
                                        link.download = doc.file_name || "document";
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      className="h-7 px-2 text-xs"
                                      data-testid={`download-doc-${docIdx}`}
                                    >
                                      <Download className="h-3 w-3 mr-1" />
                                      Download
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">No file</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Initial State */}
      {!searchResults && !error && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-8 text-center">
            <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Enter an email or name to search for documents</p>
            <p className="text-sm text-gray-400 mt-2">Results will include all documents across businesses and submissions</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
